/**
 * The Search Progress tab's date range and day / week / month rollup.
 *
 * Everything here is a PURE FUNCTION over data the report already carries. No
 * request, no new wire field: the report ships `stage_transitions_by_day` and
 * `outreach_activity` at day resolution, and a week is a sum of days. A
 * server round-trip per granularity click would add latency and an endpoint for
 * arithmetic the browser can do on data already in its hands.
 *
 * THE TRAP THIS MODULE EXISTS TO CLOSE (and the one that has already bitten this
 * epic twice) IS `sliceProgress`. The cumulative chart starts each stage at its
 * BASELINE rather than at zero, because the daily series is capped at 366 days
 * and a search open for three years would otherwise under-report every stage
 * across its whole visible range. Filtering to a sub-range has exactly the same
 * shape one level down: at the range's first day the cumulative must be the
 * baseline PLUS every transition between the window start and the range start.
 * A slice that only cut the arrays would restart every line at zero and quietly
 * report a mature search as a brand-new one. `sliceProgress` folds the dropped
 * head into the baseline for precisely this reason, which is also why the
 * cumulative fold itself (`cumulativeFrom`) needed no change: it already reads
 * the baseline, so keeping the baseline honest keeps the chart honest.
 *
 * ORDER IS LOAD-BEARING: slice, then bucket, then fold. Bucketing first would
 * put the range's edges in the middle of a week; folding first would make the
 * bucket a sum of running totals, which is a number with no meaning.
 *
 * THE RELATIVE-RANGE SHORTCUTS ANCHOR TO THE DATA, NOT TO THE CLOCK. Nothing in
 * this module reads `Date.now()`, and that is a requirement rather than a
 * coincidence: "Last 30 days" measured from today is an empty chart for every
 * search whose activity stopped months ago, which is most closed searches and
 * exactly the ones this tab gets opened to review. See `shortcutStart`.
 */

import type { SearchReportDay } from "@nu-assist/contracts";

import { DAY_MS, dayLabel, dayToMs, isDayKey, monthYearLabel, msToDay } from "./dayAxis";
import type { StageProgress, StageSeries } from "./stageProgress";

/**
 * The two buckets the granularity switch offers.
 *
 * `day` WAS the third and the default until 2026-08-27. Jose, on the 08-25 call:
 * *"the group by we should remove the day. It should be week and month only."*
 * The reason is the same one that already picked the granularity for each range
 * chip (FR3.9c): a day axis over a range measured in months is a row of
 * one-pixel spikes that reads as an empty chart, and the ranges that survived
 * the same pass are three months, six months and a year. There is no longer a
 * span short enough for a daily view to be the legible one.
 *
 * Removed from the TYPE, not merely from the switch, so the compiler finds every
 * site that still branches on it — `bucketKey`, `bucketLabel`, the two
 * `bucket*` folds and `describeProgressView` each carried a `day` arm that would
 * otherwise have sat there as unreachable code that still looked like a
 * supported option.
 */
export type Granularity = "week" | "month";

/** In the order the switch renders them, coarsest last. */
export const GRANULARITIES: readonly Granularity[] = ["week", "month"] as const;

/**
 * Narrows an arbitrary URL value; anything else reads as `week`.
 *
 * `?bucket=day` is now "anything else", and that is the point of leaving the
 * narrowing here rather than trusting the switch: links written before
 * 2026-08-27 carry it, and a value the UI can no longer offer must resolve to
 * something the UI can show. It falls back like any other junk value.
 */
export function isGranularity(value: string | null): value is Granularity {
  return value === "week" || value === "month";
}

/**
 * The Monday of the ISO week containing `day`.
 *
 * MONDAY, EXPLICITLY. "Week" is ambiguous — the US convention starts on Sunday,
 * ISO 8601 on Monday — and a silently-chosen Sunday looks like a bug to whoever
 * expects the other. `getUTCDay` numbers Sunday 0 through Saturday 6, so
 * `(dow + 6) % 7` re-bases it to Monday 0 through Sunday 6, and that value is
 * exactly how many days back the week started.
 */
export function isoWeekStart(day: string): string {
  const ms = dayToMs(day);
  const mondayBased = (new Date(ms).getUTCDay() + 6) % 7;
  return msToDay(ms - mondayBased * DAY_MS);
}

/** The first day of the calendar month containing `day`. */
export function monthStart(day: string): string {
  return `${day.slice(0, 7)}-01`;
}

/**
 * The bucket a day belongs to, as a day key.
 *
 * Deliberately still a `YYYY-MM-DD` string rather than a `2026-W30` label: the
 * axis, the tick logic and the tooltip all do calendar arithmetic on day keys,
 * and a bucket that stops being one would need a parallel implementation of
 * every one of them.
 */
export function bucketKey(day: string, granularity: Granularity): string {
  return granularity === "week" ? isoWeekStart(day) : monthStart(day);
}

/**
 * What the tooltip calls a bucket: the week it opens, or its month.
 *
 * The week form names its Monday rather than a `2026-W30` ordinal, which is
 * unreadable to anyone who does not already think in ISO week numbers — and the
 * reader this chart is for thinks in "the week we sent the first batch".
 */
export function bucketLabel(day: string, granularity: Granularity): string {
  return granularity === "week" ? `Week of ${dayLabel(day)}` : monthYearLabel(day);
}

/**
 * `2026-03-31` minus one month is not `2026-02-31`.
 *
 * `Date.UTC` would roll that over to March 3rd, so a "Last 1 month" pressed on
 * the 31st would resolve to a range that starts AFTER the month it names. The
 * day is clamped to the target month's own length instead, which is what every
 * calendar means by "a month ago".
 */
function addMonths(day: string, delta: number): string {
  const [year, month, date] = day.split("-").map(Number);
  const target = new Date(Date.UTC(year, month - 1 + delta, 1));
  const lastOfTarget = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();
  return msToDay(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), Math.min(date, lastOfTarget)),
  );
}

/** One relative-range chip. */
export interface RangeShortcut {
  /** Stable id — the test hook, and what marks the chip active. */
  id: string;
  label: string;
  /** Span length, in `unit`s. */
  amount: number;
  /**
   * The unit `amount` counts, NOT the chart's granularity — `bucket` is that.
   * `Last 3 months` is expressed as 84 days rather than 3 months, deliberately;
   * see `RANGE_SHORTCUTS`.
   */
  unit: "day" | "month";
  /**
   * The granularity the chip also selects (FR3.9c). NOT a lock — the switch
   * stays free afterwards; this is a starting point that is legible.
   */
  bucket: Granularity;
}

/**
 * The relative-range chips: three months, six months, a year.
 *
 * CUT FROM FIVE TO THREE ON 2026-08-27, and the cut is the requirement rather
 * than a tidy-up. Jared, on the 08-25 call: *"Let's just do last three months, 6
 * months, and year… no matter how short it is, we'll never need to do this short
 * of a range"*, with the reason stated a sentence later — *"there's so much
 * empty space here… I just try to compact it a little bit"*. `Last 7 days` and
 * `Last 30 days` went, and `Last 12 weeks` was RELABELLED rather than deleted:
 * it is the same 84-day span he means by "three months" and it is the one that
 * buckets into exactly 12 clean weekly points, where a calendar quarter gives
 * 13 with two partial ones at the edges. Keeping the arithmetic and changing the
 * label is what makes the chip say what it does.
 *
 * An earlier pass had already culled two candidates for resolving to the same
 * view as a survivor, and that reasoning still holds: `Last 12 months` was
 * byte-identical to `Last 1 year` (both `addMonths(anchor, -12)`, both Month),
 * and `Last 90 days` was 84 days' near-twin at the same granularity. A test
 * still asserts no two chips produce the same view.
 *
 * `Last 1 year` resolves to a 365-day span, which fits inside the 366-day cap on
 * the underlying series with a day to spare. That is the whole reason it is the
 * longest chip: the next step up cannot be answered from the cached report at
 * all, so a `Last 2 years` button would draw 366 days and label them two years.
 * A test pins the span and asserts no chip exceeds the cap, so the day the cap
 * moves the chip set has to be reconsidered deliberately rather than silently.
 */
export const RANGE_SHORTCUTS: readonly RangeShortcut[] = [
  // 12 weeks as 84 days: the span is the same and it keeps the arithmetic to
  // one unit rather than adding a third that only ever means "times seven".
  // Labelled in months because that is the unit Jared asked in.
  { id: "12w", label: "Last 3 months", amount: 84, unit: "day", bucket: "week" },
  { id: "6m", label: "Last 6 months", amount: 6, unit: "month", bucket: "month" },
  { id: "1y", label: "Last 1 year", amount: 12, unit: "month", bucket: "month" },
] as const;

/**
 * Where a shortcut's range starts, counting back from the data's LAST DAY.
 *
 * ANCHORED TO THE DATA, NOT TO TODAY (FR3.9a), and this is the decision that
 * makes the feature useful rather than decorative. "Last 30 days" measured from
 * today is an empty chart for every search whose activity stopped months ago —
 * which is most closed searches, and precisely the ones somebody opens this tab
 * to review. Anchored to the last day with data it means "the last 30 days of
 * activity on this search", which is the question actually being asked. The
 * pickers then show the resolved dates, so the anchor is visible rather than
 * left for the reader to infer.
 *
 * The result is NOT clamped here — `resolveRange` owns the bounds, and having
 * two places clamp is how they end up disagreeing.
 */
export function shortcutStart(shortcut: RangeShortcut, anchor: string): string {
  if (shortcut.unit === "month") {
    // +1 day so the span is inclusive: 6 months back from Jul 24 is Jan 25,
    // and Jan 25 → Jul 24 is six months, not six months and a day.
    return msToDay(dayToMs(addMonths(anchor, -shortcut.amount)) + DAY_MS);
  }
  return msToDay(dayToMs(anchor) - (shortcut.amount - 1) * DAY_MS);
}

/**
 * Which chip, if any, describes the range currently on screen.
 *
 * Compared by RESOLVED VALUE rather than by remembering which chip was pressed,
 * so the active state survives a reload, a shared link and a hand-typed date
 * that happens to land on the same range. It also means a chip whose span
 * exceeds the data reads as active once its clamped range matches — pressing
 * `Last 1 year` on a three-month search really does show the full range, and
 * saying otherwise would be the UI disagreeing with itself.
 */
export function activeShortcutId(
  from: string,
  to: string,
  granularity: Granularity,
  bounds: { first: string; last: string },
): string | null {
  if (to !== bounds.last) return null;
  // The full range belongs to the `Full range` chip, not to whichever shortcut
  // happens to clamp onto it. On a short search several of them do — `Last 6
  // months` and `Last 1 year` both collapse to the whole thing — and lighting
  // the first match would highlight a chip the reader did not press while the
  // one they did looks inactive. Returning null keeps the two aria-pressed
  // states mutually exclusive, which is what a set of choices should be.
  if (from === bounds.first) return null;
  for (const shortcut of RANGE_SHORTCUTS) {
    if (shortcut.bucket !== granularity) continue;
    const start = shortcutStart(shortcut, bounds.last);
    const clamped = start < bounds.first ? bounds.first : start;
    if (clamped === from) return shortcut.id;
  }
  return null;
}

/** A range the reader asked for, resolved against what the data can answer. */
export interface RangeSelection {
  /** First plotted day, always inside the data's own bounds. */
  from: string;
  /** Last plotted day, always inside the data's own bounds. */
  to: string;
  /** True when the request was not honoured verbatim. */
  adjusted: boolean;
  /** Why, in one sentence, or null when nothing was changed. */
  notice: string | null;
}

/**
 * Resolve `?from=` / `?to=` against the bounds of the series that has to answer.
 *
 * Three rules, and the second one is the FR that names it:
 *
 * 1. **Absent means everything.** A reader who never touches the pickers sees
 *    the whole range (FR3.2), so the canonical URL for this tab stays the bare
 *    one and no default has to be spelled twice.
 * 2. **An inverted range is REJECTED, never swapped.** `from=2026-08-01` with
 *    `to=2026-07-01` is not a range with its ends in the wrong order — it is a
 *    reader who mis-typed, or a link someone hand-edited, and quietly showing
 *    them July-to-August would answer a question nobody asked. It falls back to
 *    the full range and says so.
 * 3. **Nothing may reach past the data.** The series is capped at 366 days, so a
 *    range that starts before the first day cannot be answered — a flat line
 *    from January would imply zero activity where the truth is "we do not
 *    know". Out-of-bounds ends clamp and the clamp is stated (FR3.8).
 */
export function resolveRange(
  requestedFrom: string | null,
  requestedTo: string | null,
  bounds: { first: string; last: string }
): RangeSelection {
  const notices: string[] = [];

  const askedFrom = requestedFrom && requestedFrom.length > 0 ? requestedFrom : null;
  const askedTo = requestedTo && requestedTo.length > 0 ? requestedTo : null;

  let from = askedFrom;
  let to = askedTo;

  if (from !== null && !isDayKey(from)) {
    notices.push(`“${from}” isn’t a date we can read, so the start is back at the earliest day.`);
    from = null;
  }
  if (to !== null && !isDayKey(to)) {
    notices.push(`“${to}” isn’t a date we can read, so the end is back at the latest day.`);
    to = null;
  }

  // Checked BEFORE clamping, so an inversion is reported as the inversion it is
  // rather than as whatever the clamp happened to turn it into.
  if (from !== null && to !== null && from > to) {
    notices.push(
      `${from} is after ${to}, so that range can’t be read — showing the full range instead.`
    );
    from = null;
    to = null;
  }

  if (from !== null && from < bounds.first) {
    notices.push(`There’s no data before ${bounds.first}, so the range starts there.`);
    from = null;
  }
  if (to !== null && to > bounds.last) {
    notices.push(`There’s no data after ${bounds.last}, so the range ends there.`);
    to = null;
  }
  // The far ends are clamped rather than rejected: a `from` past the last day is
  // an empty range, not an unreadable one, and FR3.6 wants that named.
  if (from !== null && from > bounds.last) from = bounds.last;
  if (to !== null && to < bounds.first) to = bounds.first;

  const resolved = { from: from ?? bounds.first, to: to ?? bounds.last };
  return {
    ...resolved,
    adjusted: notices.length > 0,
    notice: notices.length > 0 ? notices.join(" ") : null,
  };
}

/**
 * Narrow a `StageProgress` to `[from, to]`, carrying the dropped head into the
 * baseline.
 *
 * ****THIS IS THE FR3.5 GUARD.**** The `carried` loop below is the entire
 * mechanism: without it every cumulative line restarts at zero the moment a
 * reader touches a date picker, and a search with 700 candidates identified
 * before the range start renders as a search with none. The daily chart would
 * look right the whole time, which is what makes the bug easy to ship.
 */
export function sliceProgress(progress: StageProgress, from: string, to: string): StageProgress {
  if (progress.days.length === 0) return progress;

  const startIndex = progress.days.findIndex((day) => day >= from);
  let endIndex = -1;
  for (let i = progress.days.length - 1; i >= 0; i -= 1) {
    if (progress.days[i] <= to) {
      endIndex = i;
      break;
    }
  }

  const baseline: Record<string, number> = { ...progress.baseline };

  // The range selects nothing at all — every day is outside it. Everything that
  // exists is therefore "before", so it ALL becomes baseline: the cumulative a
  // reader gets back by widening the range again has to be the same number.
  if (startIndex === -1 || endIndex === -1 || startIndex > endIndex) {
    for (const series of progress.series) {
      const carried = series.counts.reduce((sum, n) => sum + n, 0);
      if (carried > 0) baseline[series.stage] = (baseline[series.stage] ?? 0) + carried;
    }
    return { days: [], series: [], baseline };
  }

  const series: StageSeries[] = [];
  for (const entry of progress.series) {
    // Everything strictly before the range start is not lost, it is carried.
    let carried = 0;
    for (let i = 0; i < startIndex; i += 1) carried += entry.counts[i];
    if (carried > 0) baseline[entry.stage] = (baseline[entry.stage] ?? 0) + carried;

    const counts = entry.counts.slice(startIndex, endIndex + 1);
    const total = counts.reduce((sum, n) => sum + n, 0);
    // A stage with no entries inside the range keeps its baseline and loses its
    // DAILY line — same rule `stageProgressFrom` applies to a stage with no rows
    // at all. `cumulativeFrom` still draws it, flat at its baseline, which is
    // the weaker and truer claim.
    if (total > 0) series.push({ stage: entry.stage, counts, total });
  }
  series.sort((a, b) => b.total - a.total || a.stage.localeCompare(b.stage));

  return { days: progress.days.slice(startIndex, endIndex + 1), series, baseline };
}

/**
 * Roll a day-resolution `StageProgress` up into weeks or months.
 *
 * Totals are conserved by construction — a bucket is a sum of its days and
 * nothing is dropped — so switching granularity can move the peak but never the
 * legend's per-stage total. That is the invariant the manual browser pass
 * checks ("a month bucket equals the sum of its days") and the one a reader
 * would notice breaking first.
 *
 * The baseline passes through untouched: it describes what happened BEFORE the
 * window, which no amount of re-bucketing inside the window can change.
 */
export function bucketProgress(progress: StageProgress, granularity: Granularity): StageProgress {
  if (progress.days.length === 0) return progress;

  const days: string[] = [];
  const slotOfDay: number[] = [];
  for (const day of progress.days) {
    const key = bucketKey(day, granularity);
    // `progress.days` is contiguous and ascending, so each bucket key arrives as
    // one unbroken run and comparing against the last is enough.
    if (days.length === 0 || days[days.length - 1] !== key) days.push(key);
    slotOfDay.push(days.length - 1);
  }

  const series = progress.series.map((entry) => {
    const counts = new Array<number>(days.length).fill(0);
    entry.counts.forEach((count, index) => {
      counts[slotOfDay[index]] += count;
    });
    return { stage: entry.stage, counts, total: entry.total };
  });

  return { days, series, baseline: progress.baseline };
}

/** Outreach days inside `[from, to]`, plus how many were dropped off the front. */
export interface OutreachSlice {
  days: SearchReportDay[];
  /** Outreaches on days before `from` — added to the report's own outside-window count. */
  droppedBefore: number;
}

/**
 * The outreach counterpart of {@link sliceProgress}.
 *
 * Far simpler, and the reason is worth stating: no outreach chart is
 * cumulative, so there is no baseline to keep honest — a day outside the range
 * is genuinely not part of the answer. What it still owes the reader is the
 * COUNT it removed, because the panel prints "N logged before this window" and
 * a filtered chart that kept quoting the unfiltered number would be off by
 * however much the reader just filtered out.
 */
export function sliceOutreach(
  days: readonly SearchReportDay[],
  from: string,
  to: string
): OutreachSlice {
  let droppedBefore = 0;
  const kept: SearchReportDay[] = [];
  for (const day of days) {
    if (day.day < from) {
      droppedBefore += day.total;
      continue;
    }
    if (day.day > to) continue;
    kept.push(day);
  }
  return { days: kept, droppedBefore };
}

/**
 * Roll outreach days up into weeks or months.
 *
 * `by_method` is sparse inside each day (a method with no outreach that day is
 * absent, not zero), so the merge adds into whatever keys it finds rather than
 * assuming a fixed method set — Thrive's contact-method vocabulary is
 * tenant-configurable and a fifth value is a normal event.
 */
export function bucketOutreach(
  days: readonly SearchReportDay[],
  granularity: Granularity
): SearchReportDay[] {
  const buckets: SearchReportDay[] = [];
  for (const day of days) {
    const key = bucketKey(day.day, granularity);
    let bucket = buckets[buckets.length - 1];
    if (!bucket || bucket.day !== key) {
      bucket = { day: key, total: 0, by_method: {} };
      buckets.push(bucket);
    }
    bucket.total += day.total;
    for (const [method, count] of Object.entries(day.by_method ?? {})) {
      bucket.by_method[method] = (bucket.by_method[method] ?? 0) + count;
    }
  }
  return buckets;
}
